import {
  AssessmentAverageResult,
  AssessmentAverageSubjectResult,
  AssessmentEntry,
  AssessmentMethod,
  AssessmentMethodType,
  AssessmentsGrade,
  AssessmentsGradeOption,
  isAssessmentEntryGrade,
  isAssessmentEntryScore,
  isAssessmentMethodGrade,
  isAssessmentMethodScore,
  SubjectAverageScore,
} from '@schooly/api';
import { round } from 'lodash';

import { MarkbookBaseline, MarkbookStudentWithEntries } from './types';
import { MARKBOOK_TUTOR_GROUP } from './utils';

export type CellColorProps = {
  studentRelationId: string;
  assessmentId: string;
  groupId: string;
  subjectId: string;
  entry: AssessmentEntry;
  method: AssessmentMethod;
};

export type AverageColorProps = {
  studentRelationId: string;
  average: SubjectAverageScore;
};

export const COLOR_SCALE = {
  maxNegative: 'rgba(253, 191, 191, 1)',
  mediumNegative: 'rgba(253, 191, 191, 0.7)',
  lightestNegative: 'rgba(253, 191, 191, 0.4)',
  neutral: 'rgba(231, 246, 255, 1)',
  lightestPositive: 'rgba(183, 231, 203, 0.4)',
  mediumPositive: 'rgba(183, 231, 203, 0.7)',
  maxPositive: 'rgba(183, 231, 203, 1)',
} as const;

const COLOR_INDEX = Object.keys(COLOR_SCALE).reduce(
  (acc, key, index) => ({ ...acc, [key]: index }),
  {} as Record<keyof typeof COLOR_SCALE, number>,
);

const POSITIVE_COLORS = {
  DARK_GREEN: COLOR_INDEX.maxPositive,
  GREEN: COLOR_INDEX.mediumPositive,
  LIGHT_GREEN: COLOR_INDEX.lightestPositive,
};

const NEGATIVE_COLORS = {
  DARK_RED: COLOR_INDEX.maxNegative,
  RED: COLOR_INDEX.mediumNegative,
  LIGHT_RED: COLOR_INDEX.lightestNegative,
};

const NEUTRAL_COLOR = COLOR_INDEX.neutral;

function getPositiveColorPattern(count: number): number[] {
  if (count === 1) return [POSITIVE_COLORS.DARK_GREEN];
  if (count === 2) return [POSITIVE_COLORS.DARK_GREEN, POSITIVE_COLORS.LIGHT_GREEN];
  return [POSITIVE_COLORS.DARK_GREEN, POSITIVE_COLORS.GREEN, POSITIVE_COLORS.LIGHT_GREEN];
}

function getNegativeColorPattern(count: number): number[] {
  if (count === 1) return [NEGATIVE_COLORS.DARK_RED];
  if (count === 2) return [NEGATIVE_COLORS.LIGHT_RED, NEGATIVE_COLORS.DARK_RED];
  return [NEGATIVE_COLORS.LIGHT_RED, NEGATIVE_COLORS.RED, NEGATIVE_COLORS.DARK_RED];
}

function isNegativeColor(color: string): boolean {
  return (
    color === COLOR_SCALE.maxNegative ||
    color === COLOR_SCALE.mediumNegative ||
    color === COLOR_SCALE.lightestNegative
  );
}

function isPositiveColor(color: string): boolean {
  return (
    color === COLOR_SCALE.maxPositive ||
    color === COLOR_SCALE.mediumPositive ||
    color === COLOR_SCALE.lightestPositive
  );
}

const createColorThresholds = (maxScore: number, referenceScore: number): number[] => {
  const positiveColorsCount = 3;
  const negativeColorsCount = 3;
  const neutralColorRangePercent = 10;

  const neutralColorRange = (maxScore * neutralColorRangePercent) / 100;
  const neutralColorRangeStart = Math.max(0, referenceScore - neutralColorRange / 2);
  const neutralColorRangeEnd = Math.min(maxScore, referenceScore + neutralColorRange / 2);

  const negativeStep = neutralColorRangeStart / negativeColorsCount;
  const positiveStep = (maxScore - neutralColorRangeEnd) / positiveColorsCount;

  return [
    0,
    negativeStep,
    negativeStep * 2,
    neutralColorRangeStart,
    neutralColorRangeEnd,
    neutralColorRangeEnd + positiveStep,
    neutralColorRangeEnd + positiveStep * 2,
  ];
};

export const createScoreColorFinder = (maxScore: number, referenceScore: number) => {
  const thresholds = createColorThresholds(maxScore, referenceScore);

  return (studentScore: number): number => {
    if (studentScore > maxScore || studentScore < 0) return -1;

    const neutralStart = thresholds[3];
    const neutralEnd = thresholds[4];
    if (studentScore >= neutralStart && studentScore <= neutralEnd) {
      return 3;
    }

    for (let i = thresholds.length - 1; i >= 0; i--) {
      if (studentScore >= thresholds[i]) {
        return i;
      }
    }

    return 0;
  };
};

export const createPercentageColorFinder = (referencePercent: number) => {
  const neutralColorRangePercent = 10;

  const positiveColorsCount = Object.entries(POSITIVE_COLORS).length;
  const negativeColorsCount = Object.entries(NEGATIVE_COLORS).length;

  const range = (100 - neutralColorRangePercent) / 2;

  const negativeRange = Array.from({ length: negativeColorsCount }).map(
    (_, i) => i * (range / negativeColorsCount),
  );

  const positiveRange = Array.from({ length: positiveColorsCount }).map(
    (_, i) => i * (range / positiveColorsCount) + range + neutralColorRangePercent,
  );

  const thresholds = [...negativeRange, range, ...positiveRange].map((v) => v - 50);

  return (studentScoreInPercent: number): number => {
    const diffPercent = studentScoreInPercent - referencePercent;
    for (let i = thresholds.length - 1; i >= 0; i--) {
      if (diffPercent >= thresholds[i]) {
        return i;
      }
    }

    return 0;
  };
};

const getGradeValueRanges = (values: number[]) => {
  const maxValue = values[0];

  const lowerBounds = values.map((value, i, arr) => {
    const nextValue = arr[i + 1];
    if (!nextValue) return value;

    const gapToNext = value - nextValue;
    const halfGap = round(gapToNext / 2, 2);
    return value - halfGap;
  });

  return lowerBounds.map((lowerBound, i, arr) => {
    if (i === 0) return [lowerBound, maxValue];
    const prevValue = arr[i - 1];
    return [lowerBound, prevValue - 0.01];
  });
};

export const createGradeFinder = (gradeOptions: AssessmentsGradeOption[]) => {
  const ranges = getGradeValueRanges(gradeOptions.map((grade) => grade.value));

  return (value: number): AssessmentsGradeOption => {
    const roundedValue = round(value, 2);
    const gradeIdx = ranges.findIndex(
      (range) => roundedValue >= range[0] && roundedValue <= range[1],
    );
    const isOutOfRange = gradeIdx === -1;
    const outOfRangeIdx = value > gradeOptions[0].value ? 0 : gradeOptions.length - 1;
    const actualIdx = isOutOfRange ? outOfRangeIdx : gradeIdx;
    return gradeOptions[actualIdx];
  };
};

const getColorsWithDuplicates = (targetLength: number, arr: number[]): number[] => {
  const n = arr.length;

  if (targetLength <= n) return arr.slice(0, targetLength);

  let i = 0;
  const result = [...arr];

  while (result.length < targetLength) {
    result.push(arr[i]);
    i++;
    if (i >= arr.length) i = 0;
  }

  return result.sort((a, b) => b - a);
};

export const createGradeColorFinder = (
  gradeOptions: AssessmentsGradeOption[],
  neutralGrade: AssessmentsGradeOption,
) => {
  const actualNeutralIdx = gradeOptions.indexOf(neutralGrade);
  const totalPositive = actualNeutralIdx;
  const totalNegative = gradeOptions.length - actualNeutralIdx - 1;

  const positiveColors = getPositiveColorPattern(totalPositive);
  const positiveColorsArray =
    totalPositive > positiveColors.length
      ? getColorsWithDuplicates(totalPositive, positiveColors.reverse())
      : positiveColors;

  const negativeColors = getNegativeColorPattern(totalNegative);
  const negativeColorsArray =
    totalNegative > negativeColors.length
      ? getColorsWithDuplicates(totalNegative, negativeColors)
      : negativeColors;

  return (studentGradeValue: number): number => {
    const studentGradeIdx = gradeOptions.findIndex((grade) => grade.value === studentGradeValue);
    if (studentGradeIdx === -1) return -1;

    if (studentGradeIdx === actualNeutralIdx) return NEUTRAL_COLOR;

    if (studentGradeIdx < actualNeutralIdx) {
      return positiveColorsArray[studentGradeIdx];
    }

    const offsetIdx = studentGradeIdx - actualNeutralIdx - 1;
    return negativeColorsArray[offsetIdx];
  };
};

export const mapColorIdxToColor = (colorIdx: number): string | null => {
  if (colorIdx < 0) return null;

  const clampedIdx = Math.min(Math.max(colorIdx, 0), Object.keys(COLOR_SCALE).length - 1);

  return Object.values(COLOR_SCALE)[clampedIdx];
};

const createGradeFinders = (lists: Record<string, AssessmentsGrade>) => {
  const finders = new Map<string, ReturnType<typeof createGradeFinder>>();

  Object.entries(lists ?? {}).forEach(([selectListId, grade]) => {
    finders.set(selectListId, createGradeFinder(grade.options));
  });

  return finders;
};

export const getAssessmentEntryColorFactory = (
  averages: AssessmentAverageResult[],
  gradeLists: Record<string, AssessmentsGrade>,
  gradeFinders: Map<string, ReturnType<typeof createGradeFinder>> = createGradeFinders(gradeLists),
) => {
  const scoreColorFinders = new Map<string, ReturnType<typeof createScoreColorFinder>>();
  const gradeColorFinders = new Map<string, ReturnType<typeof createGradeColorFinder>>();

  type CreateFinderKeyParams = {
    assessmentId: string;
    groupId: string;
    methodId: string;
  };

  const createFinderKey = ({ assessmentId, groupId, methodId }: CreateFinderKeyParams): string =>
    `${assessmentId}:${groupId}:${methodId}`;

  averages.forEach((avg) => {
    // Create score color finder for score-based assessments
    if (isAssessmentMethodScore(avg.method)) {
      const scoreOutOf = avg.method.score_out_of;
      const averageValue = avg.average_value;

      if (!scoreOutOf || !averageValue) return;

      for (const groupId of avg.group_ids) {
        const key = createFinderKey({
          assessmentId: avg.assessment_id,
          groupId,
          methodId: avg.method.id,
        });
        scoreColorFinders.set(key, createScoreColorFinder(scoreOutOf, averageValue));
      }
    }

    // Create grade color finder for grade-based assessments
    if (isAssessmentMethodGrade(avg.method)) {
      if (!avg.method.select_list_id || !avg.average_value) return;
      const gradeListId = avg.method.select_list_id;
      const gradeOptions = gradeLists[gradeListId]?.options;
      const neutralGrade = gradeFinders.get(gradeListId)?.(avg.average_value);
      if (!neutralGrade) return;

      for (const groupId of avg.group_ids) {
        const key = createFinderKey({
          assessmentId: avg.assessment_id,
          groupId,
          methodId: avg.method.id,
        });
        gradeColorFinders.set(key, createGradeColorFinder(gradeOptions, neutralGrade));
      }
    }
  });

  return ({ assessmentId, groupId, entry }: CellColorProps): string | null => {
    if (entry.method.type === AssessmentMethodType.Comment) return null;

    const avg = averages.find(
      (a) =>
        a.assessment_id === assessmentId &&
        a.group_ids.includes(groupId) &&
        a.method.id === entry.method.id,
    );

    const averageValue = avg?.average_value;
    if (!averageValue) return null;

    if (isAssessmentEntryScore(entry)) {
      const key = createFinderKey({ assessmentId, groupId, methodId: avg.method.id });
      const getScoreColor = scoreColorFinders.get(key);
      if (!getScoreColor) return null;
      const colorIdx = getScoreColor(entry.score);

      return mapColorIdxToColor(colorIdx);
    }

    if (isAssessmentEntryGrade(entry)) {
      const gradeListId = avg.method.select_list_id;
      if (!gradeListId) return null;

      const gradeList = gradeLists?.[gradeListId];
      if (!gradeList?.options?.length) return null;

      const studentOptionValue = gradeList.options.find(
        (opt) => opt.id === entry.select_list_option_id,
      )?.value;

      if (typeof studentOptionValue !== 'number') return null;

      const key = createFinderKey({ assessmentId, groupId, methodId: avg.method.id });
      const getGradeColor = gradeColorFinders.get(key);
      if (!getGradeColor) return null;
      const colorIdx = getGradeColor(studentOptionValue);

      return mapColorIdxToColor(colorIdx);
    }

    return null;
  };
};

const createAverageFinderKey = ({ subject_id }: { subject_id: string | null }): string =>
  `${subject_id ?? MARKBOOK_TUTOR_GROUP.id}`;

export const getAverageColorFactory = (averages: AssessmentAverageSubjectResult[]) => {
  const colorFinders = new Map<string, ReturnType<typeof createPercentageColorFinder>>();

  for (const average of averages) {
    if (average.average_score === null) continue;
    colorFinders.set(
      createAverageFinderKey(average),
      createPercentageColorFinder(average.average_score),
    );
  }

  return ({ average }: AverageColorProps): string | null => {
    if (average.average_score === null) return null;
    const key = createAverageFinderKey(average);
    const getColor = colorFinders.get(key);
    if (!getColor) return null;
    return mapColorIdxToColor(getColor(average.average_score));
  };
};
type BaselineFinderKeyParams = {
  studentRelationId: string;
  methodType: AssessmentMethodType;
  subjectId: string;
};

const createBaselineFinderKey = ({
  methodType,
  studentRelationId,
  subjectId,
}: BaselineFinderKeyParams): string => `${methodType}:${studentRelationId}:${subjectId}`;

type BaselineColorGetterArgs = {
  baseline: MarkbookBaseline;
  gradeLists: Record<string, AssessmentsGrade>;
  scoreColorFinders: Map<string, ReturnType<typeof createPercentageColorFinder>>;
  gradeColorFinders: Map<string, ReturnType<typeof createPercentageColorFinder>>;
  createFinderKey: (params: BaselineFinderKeyParams) => string;
};

const createAssessmentEntryColorBaselineGetter =
  ({
    baseline,
    gradeLists,
    scoreColorFinders,
    gradeColorFinders,
    createFinderKey,
  }: BaselineColorGetterArgs) =>
  ({
    assessmentId,
    entry,
    studentRelationId,
    subjectId,
    method,
  }: CellColorProps): string | null => {
    const { subject } = baseline;

    const assessment = baseline.kind === 'assessment' ? baseline.assessment : undefined;

    if (entry.method.type === AssessmentMethodType.Comment) return null;
    if (assessment && assessmentId === assessment.id) return null;
    if (subjectId !== subject.id) return null;

    if (isAssessmentEntryScore(entry)) {
      const key = createFinderKey({
        methodType: entry.method.type,
        studentRelationId,
        subjectId: subject.id,
      });

      const getScoreColor = scoreColorFinders.get(key);

      if (!getScoreColor) return null;
      const colorIdx = getScoreColor((entry.score / (method.score_out_of ?? 1)) * 100);
      return mapColorIdxToColor(colorIdx);
    }

    if (isAssessmentEntryGrade(entry)) {
      const gradeListId = method.select_list_id;
      if (!gradeListId) return null;

      const gradeList = gradeLists?.[gradeListId];
      if (!gradeList?.options?.length) return null;

      const studentOptionValue = gradeList.options.find(
        (opt) => opt.id === entry.select_list_option_id,
      )?.value;

      if (typeof studentOptionValue !== 'number') return null;

      const key = createFinderKey({
        methodType: entry.method.type,
        studentRelationId,
        subjectId: subject.id,
      });
      const getGradeColor = gradeColorFinders.get(key);
      if (!getGradeColor) return null;
      const colorIdx = getGradeColor(studentOptionValue);

      return mapColorIdxToColor(colorIdx);
    }

    return null;
  };

export const getAssessmentEntryColorGroupsBaselineFactory = (
  baseline: MarkbookBaseline,
  students: MarkbookStudentWithEntries[],
  gradeLists: Record<string, AssessmentsGrade>,
) => {
  const scoreColorFinders = new Map<string, ReturnType<typeof createPercentageColorFinder>>();
  const gradeColorFinders = new Map<string, ReturnType<typeof createPercentageColorFinder>>();

  switch (baseline.kind) {
    case 'assessment': {
      const { method, assessment, subject } = baseline;
      const baselineGroupIds = new Set(subject.groups.map((g) => g.id));

      for (const { student, assessment_entries } of students) {
        for (const entry of assessment_entries.filter(
          (e) =>
            e.method.id === method.id &&
            e.assessment_id === assessment.id &&
            baselineGroupIds.has(e.group.id),
        )) {
          const key = createBaselineFinderKey({
            methodType: entry.method.type,
            studentRelationId: student.relation_id,
            subjectId: subject.id,
          });

          switch (entry.method.type) {
            case AssessmentMethodType.Score:
              const scoreOutOf = method.score_out_of;
              const score = entry.score;

              if (!scoreOutOf || score === null) break;

              scoreColorFinders.set(key, createPercentageColorFinder((score / scoreOutOf) * 100));
              break;
            case AssessmentMethodType.Grade:
              const gradeListId = method.select_list_id;
              if (!gradeListId) break;

              const gradeList = gradeLists[gradeListId];
              if (!gradeList?.options?.length) break;

              const selectedOption = gradeList.options.find(
                (opt) => opt.id === entry.select_list_option_id,
              );

              if (!selectedOption) break;

              gradeColorFinders.set(key, createPercentageColorFinder(selectedOption.value));
              break;
          }
        }
      }

      break;
    }
    case 'average': {
      const { subject } = baseline;

      const subjectId = subject.id === MARKBOOK_TUTOR_GROUP.id ? null : subject.id;

      for (const { student, assessment_entries, subject_average_score } of students) {
        const averageScore =
          subject_average_score?.find((s) => s.subject_id === subjectId)?.average_score ?? null;

        if (averageScore === null) continue;

        for (const entry of assessment_entries) {
          const key = createBaselineFinderKey({
            methodType: entry.method.type,
            studentRelationId: student.relation_id,
            subjectId: subject.id,
          });

          switch (entry.method.type) {
            case AssessmentMethodType.Score:
              scoreColorFinders.set(key, createPercentageColorFinder(averageScore));
              break;
            case AssessmentMethodType.Grade:
              gradeColorFinders.set(key, createPercentageColorFinder(averageScore));
              break;
          }
        }
      }

      break;
    }
  }
  const getColor = createAssessmentEntryColorBaselineGetter({
    baseline,
    gradeLists,
    scoreColorFinders,
    gradeColorFinders,
    createFinderKey: createBaselineFinderKey,
  });

  return (props: CellColorProps): string | null => getColor(props);
};

export function getCellTextColor(color: string): [string, string | undefined] {
  if (isPositiveColor(color)) {
    return ['common.green1', 'common.green2'];
  }

  if (isNegativeColor(color)) {
    return ['common.wine1', color === COLOR_SCALE.maxNegative ? 'common.wine1' : 'common.wine3'];
  }

  if (color === COLOR_SCALE.neutral) {
    return ['primary.main', 'common.main3'];
  }

  return ['primary.main', undefined];
}
